
import { FormControl, Grid, TextField } from '@mui/material'
import { DatePicker, DateRangePicker, LocalizationProvider } from '@mui/x-date-pickers-pro'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import React, { FC } from 'react'
import { OptimizationDetails } from 'types'
dayjs.extend(utc)
dayjs.extend(timezone)

interface DynamicSectionProps {
  optimizationDetails: OptimizationDetails
  onDateChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onDateRangeChange: (start: Date | null, end: Date | null) => void
  onChangeOpts: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlurOpts: () => void
  onChangePercentage: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlurPercentage: () => void
  localOpts: string
  localPercentage: string
  selectedType: string
}

const DynamicSection: FC<DynamicSectionProps> = ({
  optimizationDetails,
  onDateChange,
  onDateRangeChange,
  onChangeOpts,
  onBlurOpts,
  onChangePercentage,
  onBlurPercentage,
  localOpts,
  localPercentage,
  selectedType,
}) => {
  const isSeoType = selectedType === 'seo' ? true : false
  const isInsight = selectedType === 'insight' ? true : false
  const isLeasingPercentage = selectedType === 'leasingPercentage' ? true : false
  return (
    <>
      {isSeoType ? (
        <Grid container spacing={2} sx={{ display: 'flex', alignItems: 'center' }}>
          <Grid item xs={12}>
            <TextField
              key={`seo-name`}
              label="Name"
              value={optimizationDetails.optimization?.name || ''}
              fullWidth
              margin="normal"
              InputProps={{ readOnly: true }}
              sx={{
                bgcolor: 'background.defaultdim',
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              key={`seo-opts`}
              label="# of Opts"
              type="number"
              value={localOpts}
              onChange={onChangeOpts}
              onBlur={onBlurOpts}
              fullWidth
              margin="normal"
              inputProps={{ min: 0, max: 10 }}
              sx={{
                bgcolor: 'background.defaultdim',
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <FormControl fullWidth>
                <DatePicker
                  label="Follow Up Date"
                  value={optimizationDetails.followDate ? dayjs(optimizationDetails.followDate) : null}
                  onChange={newDate => {
                    const dateString = newDate ? newDate.toISOString() : ''
                    onDateChange({ target: { value: dateString } } as React.ChangeEvent<HTMLInputElement>)
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      margin: 'normal', // ✅ Make DatePicker TextField match other fields
                      sx: { bgcolor: 'background.defaultdim' },
                    },
                  }}
                />
              </FormControl>
            </LocalizationProvider>
          </Grid>
        </Grid>
      ) : (
        <>
          {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}> */}
          <Grid container spacing={2} sx={{ display: 'flex', alignItems: 'center' }}>
            <Grid item xs={isInsight ? 12 : 6}>
              <TextField
                key={`mtb-name`}
                label="Name"
                value={optimizationDetails.optimization?.name || ''}
                fullWidth
                margin="normal"
                InputProps={{ readOnly: true }}
                sx={{
                  bgcolor: 'background.defaultdim',
                }}
              />
            </Grid>
            {!isInsight && (
              <Grid item xs={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <FormControl fullWidth>
                    <DatePicker
                      label="Follow Up Date"
                      value={optimizationDetails.followDate ? dayjs(optimizationDetails.followDate) : null}
                      onChange={newDate => {
                        const dateString = newDate ? newDate.toISOString() : ''
                        onDateChange({ target: { value: dateString } } as React.ChangeEvent<HTMLInputElement>)
                      }}
                      slotProps={{
                        textField: {
                          error: !optimizationDetails.followDate,
                          fullWidth: true,
                          margin: 'normal', // ✅ Make DatePicker TextField match other fields
                          sx: { bgcolor: 'background.defaultdim' },
                        },
                      }}
                    />
                  </FormControl>
                </LocalizationProvider>
              </Grid>
            )}
            {isInsight && (
              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateRangePicker
                    localeText={{ start: 'Start Date', end: 'End Date' }}
                    value={[
                      optimizationDetails.startDate ? dayjs(optimizationDetails.startDate) : null,
                      optimizationDetails.endDate ? dayjs(optimizationDetails.endDate) : null,
                    ]}
                    onChange={([start, end]) => {
                      const startDate = start ? start.toDate() : null
                      const endDate = end ? end.toDate() : null
                      onDateRangeChange(startDate, endDate)
                    }}
                    slotProps={{
                      textField: ({ position }) => ({
                        error: !optimizationDetails.startDate || !optimizationDetails.endDate,
                        sx: {
                          bgcolor: 'background.defaultdim',
                        },
                      }),
                    }}
                    sx={{
                      width: '100%',
                      '& .MuiMultiInputDateRangeField-separator': {
                        m: 0,
                      },
                      '& .MuiFormControl-root': {
                        m: 0,
                      },
                      '& .MuiInputBase-root': {
                        maxHeight: '56px',
                        m: 0,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            )}
            {isLeasingPercentage && (
              <Grid item xs={12}>
                {/* <TextField
                  label="Percentage"
                  type="number"
                  value={optimizationDetails.optimization?.percentage ?? ''}
                  onChange={onChangePercentage}
                  onBlur={onBlurPercentage}
                  fullWidth
                  margin="normal"
                  inputProps={{
                    min: 0,
                    max: 100,
                    step: 0.01,
                  }}
                  placeholder="Enter percent (e.g., 35.5)"
                  sx={{
                    bgcolor: 'background.defaultdim',
                  }}
                /> */}
                <TextField
                  label="Percentage"
                  type="text" // allow smooth decimal typing
                  inputMode="decimal"
                  fullWidth
                  variant="outlined"
                  value={localPercentage} // ✅ controlled by local state
                  onChange={onChangePercentage}
                  onBlur={onBlurPercentage}
                  placeholder="Enter percent (e.g., 35.5)"
                  error={!localPercentage.trim()}
                  sx={{
                    bgcolor: 'background.defaultdim',
                  }}
                  inputProps={{
                    inputMode: 'decimal',
                    pattern: '[0-9]*',
                    maxLength: 6,
                    style: {
                      MozAppearance: 'textfield',
                      appearance: 'textfield',
                    },
                  }}
                />
              </Grid>
            )}
          </Grid>
          {/* </Box> */}
        </>
      )}
    </>
  )
}

export default DynamicSection
